import React, { ChangeEvent, SyntheticEvent, useState } from "react";
import { Form } from "react-final-form";
import Image from "@Components/Image";
import FormTextField from "@Components/forms/formFields/textField/FormTextField";
import CTFieldValidator, {
  isValidZip,
  minLength,
  required,
  email,
} from "@Components/forms/helpers/FieldValidator";
import { Button } from "@Components/ui";
import { withAsync } from "@Libs/api/withAsync";
import { SnackBarType } from "@Types/SnackBar";
import { INearestPickupLocation } from "@Types/ZipCodePopUps";
import { formatZipcode } from "@Utils/checkout";
import { nextAppEnv } from "@Utils/environment";
import { triggerSnackBar } from "@wff/store/SnackBarSlice";
import { useAppSelector } from "@wff/store/hooks";
import { useAppDispatch } from "store/hooks";
import { subscribeToKlaviyoList } from "../../../api/customerApi";
import classes from "../ZipCodePopUp.module.scss";

interface IWeDeliverLayoutProps {
  noDeliverTitle: string;
  noDeliverMessage: string;
  noDeliverContinueButtonLabel: string;
  noDeliverNotifyButtonLabel: string;
  noDeliverLocationIcon: {
    url: string;
    title: string;
    width: number;
    height: number;
  };
  noDeliverChangePostalCodeText: string;
  zipcode: string;
  handleToggle: (e: SyntheticEvent<Element, Event>) => void;
  closeZipCodePopUp: (e: SyntheticEvent<Element, Event>) => void;
  switchLayout: (e: SyntheticEvent<Element, Event>) => void;
  nearestPickupLocation: INearestPickupLocation;
  isPickupAvailable: boolean;
  noDeliveryAndPickupMessage: string;
  noDeliveryAndPickupContinueButtonLabel: string;
}

export default function WeDontDeliverLayout({
  noDeliverTitle,
  noDeliverContinueButtonLabel,
  noDeliverNotifyButtonLabel,
  noDeliverLocationIcon,
  noDeliverChangePostalCodeText,
  zipcode,
  closeZipCodePopUp,
  switchLayout,
  nearestPickupLocation,
  isPickupAvailable,
  noDeliveryAndPickupMessage,
  noDeliveryAndPickupContinueButtonLabel,
}: IWeDeliverLayoutProps) {
  const dispatch = useAppDispatch();
  const [formOpen, setFormOpen] = useState(false);
  const [inputEmail, setInputEmail] = useState("");
  const [inputZipcode, setInputZipcode] = useState("");
  const [touched, setTouched] = useState(zipcode ? true : false);
  const { countryShort, languageCode } = useAppSelector(({ locale }) => locale);
  const zipCodeListId = nextAppEnv.klaviyo.zipcodeNotifyListId || "Rt32np";
  const fieldLength = countryShort === "CA" ? 7 : 5;

  const setZipHandler = (e: ChangeEvent<HTMLInputElement>) => {
    if (!touched) {
      setTouched(true);
    }
    const formattedZipcode = formatZipcode(
      e.target.value.trim(),
      countryShort
    ).trim();
    setInputZipcode(formattedZipcode);
  };

  const activeNotificationClass = formOpen
    ? "trade-area__action action action--primary"
    : "action action--secondary";

  const handleClickSubmit = async (): Promise<void> => {
    const payload = {
      profiles: [
        {
          email: inputEmail,
          postalCode: inputZipcode,
        },
      ],
    };
    const listId = zipCodeListId;
    const { error, response } = await withAsync(() =>
      subscribeToKlaviyoList(listId, payload)
    );
    const success = !error && response?.status === 200;
    const snackBarData = {
      message: success
        ? "Succesfully subscribed to notify list"
        : "Failed to subscribe to notify list",
      type: success ? SnackBarType.SUCCESS : SnackBarType.ERROR,
    };
    dispatch(triggerSnackBar(snackBarData));
    setFormOpen(!formOpen);
  };

  const handleOpenForm = () => {
    setFormOpen(!formOpen);
    setInputEmail("");
    setInputZipcode("");
  };

  return (
    <div
      className={`${classes["trade-area__content"]}`}
      data-testid="layout--we-dont-deliver"
    >
      <h2 className={classes["trade-area__title"]}>
        {`${noDeliverTitle} ${zipcode}`}
      </h2>
      <p className={classes["trade-area__copy"]}>
        {isPickupAvailable === false ? noDeliveryAndPickupMessage : ""}
      </p>
      {nearestPickupLocation && (
        <div
          className={classes["trade-area__no_nearest_pickup_location"]}
        ></div>
      )}
      <Button
        type="submit"
        className="trade-area__action action action--primary"
        onClick={closeZipCodePopUp}
      >
        {isPickupAvailable
          ? noDeliverContinueButtonLabel
          : noDeliveryAndPickupContinueButtonLabel}
      </Button>
      <div className={classes["notification"]}>
        <Button className={activeNotificationClass} onClick={handleOpenForm}>
          {!formOpen ? noDeliverNotifyButtonLabel : "Cancel"}
        </Button>
      </div>
      {formOpen ? (
        <div>
          <Form
            onSubmit={handleClickSubmit}
            render={(props) => {
              return (
                <form
                  id="zipcode-subscribe-box"
                  aria-label="zipcode email form"
                  onSubmit={props.handleSubmit}
                >
                  <div className="form__field form__field--text">
                    <FormTextField
                      name="postalCode"
                      onChange={setZipHandler}
                      data-testid="form_field-zip"
                      type="text"
                      aria-label="postalCode"
                      label={countryShort === "CA" ? "Postal Code" : "Zipcode"}
                      outlined={true}
                      maxLength={fieldLength}
                      validate={CTFieldValidator(
                        [required(), isValidZip(), minLength(5)],
                        languageCode === "fr-CA" ? "Code Postal" : "Postal Code"
                      )}
                      value={inputZipcode}
                      showTooltipIconOnErrorOnly={true}
                    />
                    <FormTextField
                      aria-label="Email"
                      name="email"
                      data-testid="form_field-email"
                      label="Email"
                      validate={CTFieldValidator(
                        [required(), email()],
                        "Email"
                      )}
                      outlined={true}
                      value={inputEmail}
                      onChange={(e) => {
                        setInputEmail(e.target.value);
                      }}
                    />
                  </div>

                  <Button
                    className="action action--primary"
                    id="email-subscribe-button"
                    data-testid="form_field-submit"
                    type="submit"
                  >
                    Submit
                  </Button>
                </form>
              );
            }}
          />
        </div>
      ) : (
        <></>
      )}
      <div className={classes["trade-area__change-location-wrapper"]}>
        <i className={classes["trade-area__change-location-pin"]}>
          <Image
            src={`${noDeliverLocationIcon?.url}?w=30&h=30`}
            width={30}
            height={30}
            alt="icon_delivery"
            data-testid="icon_delivery"
          />
        </i>
        <Button
          type="button"
          data-testid="button-switch-layout"
          className={classes["trade-area__change-location-button"]}
          onClick={switchLayout}
        >
          {noDeliverChangePostalCodeText}
        </Button>
      </div>
    </div>
  );
}
